import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';

import { VolunteerService } from '../../services/volunteer.service';
import { Volunteer } from '../../interfaces/volunteer.interfaces';
import { StatusHistoryEntry } from '../../interfaces/student.interfaces';
import { DegreeEntry } from '../../services/onboarding-state.service';
import { FolioPillComponent } from '../ui/folio-pill/folio-pill.component';
import { InactivateVolunteerModalComponent } from '../shared/inactivate-volunteer-modal/inactivate-volunteer-modal.component';

export type VolunteerDocumentField = 'ine' | 'cv' | 'comprobante';

@Component({
  selector: 'app-volunteer-detail',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    ReactiveFormsModule,
    FolioPillComponent,
    InactivateVolunteerModalComponent,
  ],
  templateUrl: './volunteer-detail.html',
  styleUrl: './volunteer-detail.scss',
})
export class VolunteerDetail implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private volunteerService = inject(VolunteerService);
  private cdr = inject(ChangeDetectorRef);
  private fb = inject(FormBuilder);

  volunteerId: string | null = null;
  volunteer: Volunteer | null = null;
  isLoading = true;
  errorMessage: string | null = null;

  isAccepting = false;
  isRejecting = false;
  showInactivateModal = false;
  statusFeedback: string | null = null;

  isEditing = false;
  isSaving = false;
  profileError: string | null = null;
  profileSuccess: string | null = null;

  isFolioCopied = false;
  private folioCopyTimeout: any = null;

  volunteerForm = this.fb.group({
    fullName: ['', Validators.required],
    mail: ['', [Validators.required, Validators.email]],
    phone: ['', Validators.required],
    curp: [''],
    occupation: [''],
    birthDate: ['', Validators.required],
    volunteerStartDate: ['', Validators.required],
    howDidYouHearAboutAbaq: [''],
    bioPersonal: [''],
    intereses: [''],
    talentos: [''],
    aportesAbaq: [''],
    animalExperience: [''],
    descExperiencia: [''],
    motivacionAbaq: [''],
    hasSecondaryContact: [false],
    secondaryPhone: [''],
    facebookUsuario: [''],
    instagramUsuario: [''],
    tiktokUsuario: [''],
    xUsuario: [''],
    youtubeUsuario: [''],
    linkedinUsuario: [''],
  });

  // Modal para edición de documentos en la nube
  showDocumentLinkModal = false;
  editingDocumentField: VolunteerDocumentField | null = null;
  editingDocumentTitle = '';
  editingDocumentSubtitle = '';
  editingDocumentUrl = '';
  documentLinkError: string | null = null;
  isSavingDocumentLink = false;

  ngOnInit(): void {
    this.volunteerId = this.route.snapshot.paramMap.get('id');

    if (!this.volunteerId) {
      this.errorMessage = 'No se encontró el identificador del voluntario.';
      this.isLoading = false;
      return;
    }

    this.loadVolunteerData();
  }

  loadVolunteerData(): void {
    if (!this.volunteerId) return;
    this.volunteerService.getVolunteer(this.volunteerId).subscribe({
      next: (volunteer) => {
        this.volunteer = volunteer;
        this.loadVolunteerForm(volunteer);
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('❌ ERROR OBTENIENDO VOLUNTARIO:', error);
        this.errorMessage = 'No se pudo cargar la información del voluntario.';
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private formatDateForInput(date: Date | string | null | undefined): string {
    if (!date) return '';
    const value = new Date(date);
    if (Number.isNaN(value.getTime())) return '';
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private loadVolunteerForm(volunteer: Volunteer): void {
    this.volunteerForm.reset({
      fullName: volunteer.fullName ?? '',
      mail: volunteer.mail ?? '',
      phone: volunteer.phone ?? '',
      curp: volunteer.curp ?? '',
      occupation: volunteer.occupation ?? '',
      birthDate: this.formatDateForInput(volunteer.birthDate),
      volunteerStartDate: this.formatDateForInput(volunteer.volunteerStartDate),
      howDidYouHearAboutAbaq: volunteer.howDidYouHearAboutAbaq ?? '',
      bioPersonal: volunteer.bioPersonal ?? '',
      intereses: volunteer.intereses ?? '',
      talentos: volunteer.talentos ?? '',
      aportesAbaq: volunteer.aportesAbaq ?? '',
      animalExperience: volunteer.animalExperience ?? '',
      descExperiencia: volunteer.descExperiencia ?? '',
      motivacionAbaq: volunteer.motivacionAbaq ?? '',
      hasSecondaryContact: volunteer.hasSecondaryContact ?? false,
      secondaryPhone: volunteer.secondaryPhone ?? '',
      facebookUsuario: volunteer.socialMedia?.facebook ?? '',
      instagramUsuario: volunteer.socialMedia?.instagram ?? '',
      tiktokUsuario: volunteer.socialMedia?.tiktok ?? '',
      xUsuario: volunteer.socialMedia?.x ?? '',
      youtubeUsuario: volunteer.socialMedia?.youtube ?? '',
      linkedinUsuario: volunteer.socialMedia?.linkedin ?? '',
    });
  }

  startEdit(): void {
    if (!this.volunteer) return;
    this.profileError = null;
    this.profileSuccess = null;
    this.loadVolunteerForm(this.volunteer);
    this.isEditing = true;
  }

  cancelEdit(): void {
    if (this.volunteer) {
      this.loadVolunteerForm(this.volunteer);
    }
    this.isEditing = false;
    this.profileError = null;
  }

  saveVolunteer(): void {
    if (!this.volunteer || !this.volunteerId || this.volunteerForm.invalid) {
      this.volunteerForm.markAllAsTouched();
      this.profileError = 'Por favor completa todos los campos requeridos correctamente.';
      return;
    }

    this.isSaving = true;
    this.profileError = null;
    this.profileSuccess = null;

    const v = this.volunteerForm.value;
    const updatePayload: any = {
      nombreCompleto: (v.fullName || '').trim(),
      correoPersonal: (v.mail || '').trim().toLowerCase(),
      telefono: (v.phone || '').trim(),
      curp: (v.curp || '').trim().toUpperCase(),
      occupation: (v.occupation || '').trim(),
      fechaNacimiento: v.birthDate ? new Date(v.birthDate) : this.volunteer.birthDate,
      fechaInicioVoluntariado: v.volunteerStartDate ? new Date(v.volunteerStartDate) : this.volunteer.volunteerStartDate,
      comoSupisteDeAbaq: (v.howDidYouHearAboutAbaq || '').trim(),
      bioPersonal: (v.bioPersonal || '').trim(),
      intereses: (v.intereses || '').trim(),
      talentos: (v.talentos || '').trim(),
      aportesAbaq: (v.aportesAbaq || '').trim(),
      animalExperience: (v.animalExperience || '').trim(),
      descExperiencia: (v.descExperiencia || '').trim(),
      motivacionAbaq: (v.motivacionAbaq || '').trim(),
      hasSecondaryContact: !!v.hasSecondaryContact,
      secondaryPhone: (v.secondaryPhone || '').trim(),
      facebookUsuario: (v.facebookUsuario || '').trim(),
      instagramUsuario: (v.instagramUsuario || '').trim(),
      tiktokUsuario: (v.tiktokUsuario || '').trim(),
      xUsuario: (v.xUsuario || '').trim(),
      youtubeUsuario: (v.youtubeUsuario || '').trim(),
      linkedinUsuario: (v.linkedinUsuario || '').trim(),
    };

    this.volunteerService.updateVolunteer(this.volunteerId, updatePayload).subscribe({
      next: (updated) => {
        this.isSaving = false;
        this.isEditing = false;
        this.volunteer = updated;
        this.profileSuccess = 'Información del expediente actualizada exitosamente en la base de datos.';
        this.cdr.detectChanges();
        setTimeout(() => (this.profileSuccess = null), 5000);
      },
      error: (err) => {
        this.isSaving = false;
        this.profileError = err?.error?.message || 'Error al guardar los cambios del voluntario.';
        this.cdr.detectChanges();
      },
    });
  }

  acceptVolunteer(): void {
    if (!this.volunteerId || this.isAccepting) return;

    const comments = window.prompt(
      `Mensaje o nota de bienvenida para ${this.volunteer?.fullName || this.volunteer?.folio || 'el voluntario general'} (se incluirá en el correo oficial y en su expediente):`,
      ''
    );
    if (comments === null) {
      // El administrador canceló la aceptación
      return;
    }

    this.isAccepting = true;
    this.statusFeedback = null;

    this.volunteerService.acceptVolunteer(this.volunteerId, comments.trim()).subscribe({
      next: (updated) => {
        this.isAccepting = false;
        this.statusFeedback =
          '¡Voluntario general aceptado exitosamente! Se emitió su constancia y se notificó a su cuenta.';
        this.volunteer = updated;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isAccepting = false;
        console.error('Error al aceptar voluntario:', err);
        this.statusFeedback =
          'Error al aceptar el voluntario. Por favor intenta de nuevo.';
        this.cdr.detectChanges();
      },
    });
  }

  rejectVolunteer(): void {
    if (!this.volunteerId || this.isRejecting) return;
    const comments = window.prompt(
      'Motivo u observaciones para el rechazo del expediente:',
      'Documentación incompleta o no legible. Favor de corregir en el registro.'
    );
    if (comments === null) return;
    this.isRejecting = true;
    this.statusFeedback = null;

    this.volunteerService.rejectVolunteer(this.volunteerId, comments.trim()).subscribe({
      next: (updated) => {
        this.isRejecting = false;
        this.statusFeedback =
          'Solicitud de ingreso del voluntario general rechazada y observaciones enviadas por correo.';
        this.volunteer = updated;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isRejecting = false;
        console.error('Error al rechazar voluntario:', err);
        this.statusFeedback =
          'Error al rechazar el voluntario. Por favor intenta de nuevo.';
        this.cdr.detectChanges();
      },
    });
  }

  openInactivateModal(): void {
    this.showInactivateModal = true;
  }

  closeInactivateModal(): void {
    this.showInactivateModal = false;
  }

  handleInactivateConfirmed(event: { comments: string }): void {
    if (!this.volunteerId) return;
    this.volunteerService.updateVolunteerAccountStatus(
      this.volunteerId,
      'inactive',
      'administrative_decision',
      event.comments,
    ).subscribe({
      next: (updated) => {
        this.volunteer = updated;
        this.closeInactivateModal();
        this.statusFeedback = 'Cuenta inactivada exitosamente por decisión administrativa.';
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error al inactivar voluntario:', err);
        this.statusFeedback = 'Error al inactivar cuenta: ' + (err?.error?.message || 'Error en el servidor');
        this.closeInactivateModal();
        this.cdr.detectChanges();
      },
    });
  }

  getFriendlyReason(reason?: string): string {
    if (!reason) return 'Decisión administrativa institucional';
    const map: Record<string, string> = {
      initial_signup: 'Registro inicial completado',
      admin_approval: 'Aprobación oficial de expediente',
      admin_rejection: 'Rechazo con observaciones de Coordinación',
      hours_completed: 'Conclusión satisfactoria de 480 horas de servicio',
      administrative_decision: 'Baja administrativa institucional',
      re_registration: 'Re-registro y corrección de expediente',
    };
    return map[reason] || reason;
  }

  /* Documentos en la nube */
  hasValidDocument(fileUrl: string | null | undefined): boolean {
    if (!fileUrl || fileUrl === 'pending') return false;
    const trimmed = fileUrl.trim();
    return trimmed.length > 0 && (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('/'));
  }

  openDocument(fileUrl: string | null | undefined): void {
    if (!fileUrl || fileUrl === 'pending') return;
    window.open(fileUrl, '_blank', 'noopener,noreferrer');
  }

  openEditDocumentLinkModal(field: VolunteerDocumentField): void {
    if (!this.volunteer) return;
    this.editingDocumentField = field;
    this.documentLinkError = null;
    const titles: Record<VolunteerDocumentField, { title: string; subtitle: string }> = {
      ine: {
        title: 'Identificación Oficial (INE)',
        subtitle: 'Ingresa el enlace público de Google Drive o OneDrive con permisos de lectura.',
      },
      cv: {
        title: 'Currículum Vitae (CV) o Semblanza',
        subtitle: 'Ingresa el enlace público del CV o perfil profesional en la nube.',
      },
      comprobante: {
        title: 'Comprobante de Domicilio o Estudios',
        subtitle: 'Ingresa el enlace público de consulta del comprobante.',
      },
    };
    this.editingDocumentTitle = titles[field].title;
    this.editingDocumentSubtitle = titles[field].subtitle;
    this.editingDocumentUrl = this.volunteer.cloudUrls?.[field] || '';
    this.showDocumentLinkModal = true;
  }

  closeDocumentLinkModal(): void {
    this.showDocumentLinkModal = false;
    this.editingDocumentField = null;
    this.editingDocumentUrl = '';
    this.documentLinkError = null;
  }

  saveDocumentLink(): void {
    if (!this.volunteer || !this.volunteerId || !this.editingDocumentField) return;
    const url = (this.editingDocumentUrl || '').trim();

    if (url.length > 0 && !url.startsWith('http://') && !url.startsWith('https://')) {
      this.documentLinkError = 'El enlace debe comenzar con https:// o http://';
      return;
    }

    this.isSavingDocumentLink = true;
    this.documentLinkError = null;

    const currentCloudUrls = { ...(this.volunteer.cloudUrls || {}) };
    currentCloudUrls[this.editingDocumentField] = url;

    this.volunteerService.updateVolunteer(this.volunteerId, { cloudUrls: currentCloudUrls }).subscribe({
      next: (updated) => {
        this.isSavingDocumentLink = false;
        this.volunteer = updated;
        this.closeDocumentLinkModal();
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.isSavingDocumentLink = false;
        this.documentLinkError = err?.error?.message || 'Error al guardar el enlace del documento.';
        this.cdr.detectChanges();
      },
    });
  }

  // Helpers para visualización de Inducción (6 módulos)
  get reflectionsList() {
    const refs = this.volunteer?.onboardingData?.reflections || {};
    const modules = [
      { id: 'modulo-1', name: 'Módulo 1: Historia, Misión y Visión de ABAQ' },
      { id: 'modulo-2', name: 'Módulo 2: Valores y Cultura del Voluntariado' },
      { id: 'modulo-3', name: 'Módulo 3: Bienestar y Rescate Animal' },
      { id: 'modulo-4', name: 'Módulo 4: Protocolos de Seguridad y Convivencia' },
      { id: 'modulo-5', name: 'Módulo 5: Adopciones Responsables y Campañas' },
      { id: 'modulo-6', name: 'Módulo 6: Organización de Eventos y Difusión' },
    ];
    return modules.map((m) => ({
      ...m,
      data: refs[m.id] || null,
    }));
  }

  hasAnyReflections(): boolean {
    const refs = this.volunteer?.onboardingData?.reflections;
    return !!refs && Object.keys(refs).length > 0;
  }

  getActivityTitle(act: string): string {
    if (!act) return '';
    return act.replace(/\s*\((?:Acredita|Horas)[\s\S]*?\)/gi, '').trim();
  }

  legalDocs: Record<string, { title: string; url: string }> = {
    reglamento: {
      title: 'Reglamento General Interno',
      url: '',
    },
    privacidad: {
      title: 'Aviso de Privacidad Integral',
      url: '',
    },
    etica: {
      title: 'Código de Ética y Conducta',
      url: '',
    },
    incognito: {
      title: 'Permiso de Difusión Institucional',
      url: '',
    },
    consentimiento: {
      title: 'Consentimiento Voluntario Expreso',
      url: '',
    },
  };

  getLegalDocUrl(docKey: string): string {
    const doc = this.legalDocs[docKey];
    return doc?.url || '#';
  }

  openLegalDoc(docKey: string, event?: Event): void {
    const doc = this.legalDocs[docKey];
    const url = doc?.url;
    if (url && url.startsWith('http')) {
      return;
    }

    if (event) {
      event.preventDefault();
    }

    const win = window.open('', '_blank');
    if (win) {
      const docTitle = doc?.title || 'Documento Normativo Institucional';
      win.document.write(`
        <!DOCTYPE html>
        <html lang="es">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${docTitle} · ABAQ</title>
          <style>
            body {
              font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              background: #f8fafc;
              color: #1e293b;
              padding: 24px;
              box-sizing: border-box;
            }
            .card {
              max-width: 540px;
              width: 100%;
              padding: 40px 32px;
              background: #ffffff;
              border-radius: 20px;
              border: 1px solid #e2e8f0;
              box-shadow: 0 16px 36px rgba(36, 54, 111, 0.08);
              text-align: center;
            }
            .badge {
              display: inline-block;
              background: #ecfdf5;
              color: #065f46;
              border: 1px solid #a7f3d0;
              padding: 5px 14px;
              border-radius: 999px;
              font-size: 11.5px;
              font-weight: 800;
              letter-spacing: 0.5px;
              margin-bottom: 20px;
              text-transform: uppercase;
            }
            h1 {
              color: #24366f;
              margin: 0 0 12px;
              font-size: 22px;
              font-weight: 800;
            }
            p {
              color: #64748b;
              line-height: 1.6;
              font-size: 14.5px;
              margin: 0 0 24px;
            }
            .info-box {
              background: #f1f5f9;
              border-radius: 12px;
              padding: 16px;
              font-size: 13px;
              color: #334155;
              border: 1px solid #cbd5e1;
            }
          </style>
        </head>
        <body>
          <div class="card">
            <span class="badge">✓ Validado en Expediente Digital</span>
            <h1>${docTitle}</h1>
            <p>Este documento normativo fue revisado y aceptado expresamente durante el proceso de registro institucional en ABAQ Querétaro.</p>
            <div class="info-box">
              📄 La versión digital descargable para consulta pública se encuentra en preparación y estará vinculada a este enlace próximamente.
            </div>
          </div>
        </body>
        </html>
      `);
      win.document.close();
    }
  }

  /* Bitácora de estatus (Fase C4.1) — última transición por fecha */
  get lastStatusChange(): StatusHistoryEntry | null {
    const history = this.volunteer?.statusHistory;
    if (!history || history.length === 0) {
      return null;
    }
    return [...history].sort((a, b) => {
      const da = a.date ? new Date(a.date).getTime() : 0;
      const db = b.date ? new Date(b.date).getTime() : 0;
      return db - da;
    })[0];
  }

  get opportunitiesCount(): number {
    return (this.volunteer as any)?.accreditations?.length || 0;
  }

  get activitiesCount(): number {
    return this.volunteer?.assignedActivities?.length || 0;
  }

  get totalStatsCount(): number {
    return this.opportunitiesCount + this.activitiesCount;
  }

  copyFolioToClipboard(event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (!this.volunteer?.folio) return;

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(this.volunteer.folio)
        .then(() => {
          this.triggerFolioCopiedState();
        })
        .catch(() => {
          this.fallbackCopyText(this.volunteer!.folio!);
        });
    } else {
      this.fallbackCopyText(this.volunteer.folio);
    }
  }

  private triggerFolioCopiedState(): void {
    this.isFolioCopied = true;
    this.cdr.detectChanges();
    if (this.folioCopyTimeout) {
      clearTimeout(this.folioCopyTimeout);
    }
    this.folioCopyTimeout = setTimeout(() => {
      this.isFolioCopied = false;
      this.cdr.detectChanges();
    }, 2000);
  }

  private fallbackCopyText(text: string): void {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.triggerFolioCopiedState();
    } catch (e) {
      console.error('No se pudo copiar el folio:', e);
    }
  }

  ngOnDestroy(): void {
    if (this.folioCopyTimeout) {
      clearTimeout(this.folioCopyTimeout);
    }
  }
}
